import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { utils } from 'ethers';

import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { TxStatus } from 'shared/constants/types';
import { generateSubSalt } from 'shared/constants/utils';
import { useAccount, usePublicClient } from 'shared/hooks/use-web3';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useRoomStore } from 'shared/store/roomStore';
import { readRounds } from 'shared/viem-functions/read-game-function';

const findMove = async (hash: `0x${string}`, salt: string, address: string) => {
  for (const move of [1, 2, 3]) {
    const computedHash = utils.solidityKeccak256(
      ['string', 'uint8', 'address'],
      [salt, move, address],
    ) as `0x${string}`;
    if (computedHash === hash) {
      return move;
    }
  }
  return null;
};

export const useRoundInfo = () => {
  const { activeRoomId } = useParams();
  const { roundCounter, closerRoom, setRoundStage, setRoundMoves, roomStatus } = useRoomStore();
  const publicClient = usePublicClient();

  const { amIPlayerA, someoneElseGame } = useRoomStore();
  const { mainSalt } = usePlayerGamesStore();
  const { setPlayMove, playMoves } = usePlayerGamesStore();
  const { address, chainId } = useAccount();

  const processRoundData = async () => {
    if (activeRoomId && address) {
      console.log('reading rounds');
      let data = await readRounds({
        provider: publicClient,
        roomId: BigInt(activeRoomId),
        roundCounter: BigInt(roundCounter),
      });
      const [moveA, moveB, encrMoveA, encrMoveB, winner, stage, roundStage] = data;
      setRoundStage(roundStage);
      Sentry.setTag('roundStage', roundStage);
      setRoundMoves({
        playerA: { encrMove: encrMoveA, move: moveA },
        playerB: { encrMove: encrMoveB, move: moveB },
      });
      const currentPlayMoveKey = `${activeRoomId}${address}${chainId}${roundCounter}`;
      const isPlayerMoveMissing =
        (amIPlayerA &&
          !moveA &&
          encrMoveA !== '0x0000000000000000000000000000000000000000000000000000000000000000' &&
          !playMoves[currentPlayMoveKey]?.move) ||
        (!amIPlayerA &&
          !moveB &&
          encrMoveB !== '0x0000000000000000000000000000000000000000000000000000000000000000' &&
          !playMoves[currentPlayMoveKey]?.move);
      if (
        isPlayerMoveMissing &&
        !someoneElseGame &&
        mainSalt?.[`${activeRoomId}${address}${chainId}`]
      ) {
        const salt = await generateSubSalt(
          mainSalt[`${activeRoomId}${address}${chainId}`] as string,
          roundCounter,
        );
        const moveHash = amIPlayerA ? encrMoveA : encrMoveB;
        const move = await findMove(moveHash, salt, address);
        if (!move) return;
        setPlayMove(currentPlayMoveKey, {
          playerAddress: address,
          salt,
          move,
          moveHash: null,
          revealHash: null,
          moveStatus: TxStatus.Confirmed,
          revealStatus: TxStatus.Submitted,
        });
      }
    }
  };

  useEffect(() => {
    processRoundData();
  }, [roundCounter, mainSalt]);

  useEffect(() => {
    if (roomStatus !== RoomStatusEnum.Closed) {
      processRoundData();
    }
  }, [closerRoom, roomStatus, roundCounter, mainSalt]);
};
